import { useFavorites } from "@/hooks/use-favorites";
import { useHaptics } from "@/hooks/use-haptics";
import { Star } from "lucide-react";

interface FormulaItem {
  name: string;
  formula: string;
  description: string;
}

interface FormulaGroup {
  category: string;
  items: FormulaItem[];
}

const allFormulas: FormulaGroup[] = [
  {
    category: "⚡ Electrical",
    items: [
      { name: "Ohm's Law", formula: "V = I × R", description: "Voltage = Current × Resistance. Rearrange: I = V/R, R = V/I" },
      { name: "Power (DC)", formula: "P = V × I", description: "Watts = Volts × Amps. Also P = I²R and P = V²/R" },
      { name: "Power (1Φ AC)", formula: "P = V × I × PF", description: "Real power with power factor. PF typically 0.8-0.95" },
      { name: "Power (3Φ AC)", formula: "P = √3 × V × I × PF", description: "Three-phase power. √3 ≈ 1.732" },
      { name: "Voltage Drop", formula: "Vd = (2 × K × I × D) / CM", description: "K=12.9 (Cu) or 21.2 (Al), I=amps, D=distance(ft), CM=circular mils" },
      { name: "Transformer Ratio", formula: "Vp/Vs = Np/Ns", description: "Primary/Secondary voltage ratio equals turns ratio" },
    ],
  },
  {
    category: "🔧 Mechanical / Plumbing",
    items: [
      { name: "Flow Rate", formula: "Q = A × V", description: "Flow = Cross-sectional Area × Velocity" },
      { name: "Pipe Area", formula: "A = π × r²", description: "Cross-sectional area. Use inside diameter / 2 for radius" },
      { name: "Head Pressure", formula: "P = 0.433 × h", description: "PSI = 0.433 × height in feet of water column" },
      { name: "BTU Heating", formula: "BTU = GPM × 500 × ΔT", description: "500 = 60min × 8.33lb/gal. ΔT = temp difference °F" },
      { name: "Boiler HP", formula: "1 BHP = 33,475 BTU/hr", description: "Or approximately 34.5 lbs of steam per hour at 212°F" },
      { name: "Pump HP", formula: "HP = (GPM × Head) / (3960 × eff)", description: "Head in feet, eff = pump efficiency (decimal)" },
    ],
  },
  {
    category: "📐 Layout & Offsets",
    items: [
      { name: "45° Offset", formula: "Travel = Offset × 1.414", description: "Multiply offset by √2. Set = Offset" },
      { name: "30° Offset", formula: "Travel = Offset × 2.0", description: "Set = Offset × 1.732 (√3)" },
      { name: "22.5° Offset", formula: "Travel = Offset × 2.613", description: "Set = Offset × 2.414" },
      { name: "60° Offset", formula: "Travel = Offset × 1.155", description: "Set = Offset × 0.577" },
      { name: "Rolling Offset", formula: "True Offset = √(Rise² + Roll²)", description: "Then use fitting angle multiplier for travel" },
      { name: "Conduit Bend", formula: "Gain = R × (1 - cos θ) × 2", description: "R=bend radius, θ=bend angle. Used for stub-ups and offsets" },
    ],
  },
  {
    category: "🏗️ General",
    items: [
      { name: "Circumference", formula: "C = π × D", description: "π ≈ 3.14159. Used for pipe wrap, insulation cuts" },
      { name: "Area of Circle", formula: "A = π × r²", description: "Radius = Diameter / 2" },
      { name: "Pythagorean", formula: "c² = a² + b²", description: "Finding diagonal or hypotenuse measurements" },
      { name: "Percent Grade", formula: "Grade = (Rise / Run) × 100", description: "Drainage: 1/4\" per foot = 2.08% grade" },
    ],
  },
];

const categoryFilterMap: Record<string, string[]> = {
  electrical: ["⚡ Electrical", "📐 Layout & Offsets"],
  pipe: ["🔧 Mechanical / Plumbing", "📐 Layout & Offsets", "🏗️ General"],
  ductwork: ["🔧 Mechanical / Plumbing", "🏗️ General"],
  structural: ["🏗️ General"],
};

const makeId = (cat: string, name: string) => `formula-${cat}-${name}`.replace(/\s+/g, "-").toLowerCase();

const TradeFormulas = ({ trade }: { trade: string }) => {
  const { lightTap } = useHaptics();
  const { toggleFavorite, isFavorite } = useFavorites();

  const relevantCategories = categoryFilterMap[trade] ?? Object.values(categoryFilterMap).flat();
  const filtered = allFormulas.filter(g => relevantCategories.includes(g.category));

  return (
    <div className="space-y-4">
      {filtered.map((cat) => (
        <div key={cat.category}>
          <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">{cat.category}</h3>
          <div className="space-y-2">
            {cat.items.map((item) => {
              const id = makeId(cat.category, item.name);
              const faved = isFavorite(id);
              return (
                <div key={item.name} className="trade-card border-l-2 border-primary pl-3 flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-foreground">{item.name}</div>
                    <div className="font-mono text-primary text-base font-bold mt-0.5">{item.formula}</div>
                    <div className="text-xs text-muted-foreground mt-1">{item.description}</div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      lightTap();
                      toggleFavorite({ id, type: "formula", name: item.name, detail: item.formula, category: cat.category });
                    }}
                    className="shrink-0 mt-1 p-1.5 rounded-md transition-colors hover:bg-secondary"
                    aria-label={faved ? "Remove from favorites" : "Add to favorites"}
                  >
                    <Star className={`w-4 h-4 transition-colors ${faved ? "fill-primary text-primary" : "text-muted-foreground"}`} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default TradeFormulas;
